/**
 * DeleteUser.jsx — Manage Users
 *
 * FIXES:
 *   - View Mode: removed Delete button from row actions (keep only 👁 View)
 *   - View Mode: fixed table header (removed duplicate Mobile + wrong Actions column)
 *   - Delete Mode: unchanged (still has Delete button)
 *   - Pagination: kept as-is
 */

import { useEffect, useState } from "react";
import { deleteonlyone, fetchAllUser } from "../..";
import { isAuthenticated } from "../../../Auth/helper";

const USERS_PER_PAGE = 10;

const DeleteUser = ({ onBack, mode = "view" }) => {
  const [alluser, setalluser] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [updateUi, setUi] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedUser, setExpandedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const isDeleteMode = mode === "delete";
  const currentuser = isAuthenticated();

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchAllUser(currentuser.user._id, currentuser.token);
        if (Array.isArray(res)) {
          setalluser(res);
          setFilteredUsers(res);
        } else {
          setError("Failed to load users.");
        }
      } catch (e) {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [updateUi]);

  // Live search
  useEffect(() => {
    const q = searchQuery.toLowerCase().trim();
    const result = !q
      ? alluser
      : alluser.filter(
          (u) =>
            u.Name?.toLowerCase().includes(q) ||
            String(u.MobileNo).includes(q) ||
            u.EmailId?.toLowerCase().includes(q) ||
            u.Address?.toLowerCase().includes(q),
        );
    setFilteredUsers(result);
    setExpandedUser(null);
    setCurrentPage(1);
  }, [searchQuery, alluser]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE,
  );
  const goToPage = (page) => {
    setCurrentPage(page);
    setExpandedUser(null);
  };

  const deleteoneuser = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      const re = await deleteonlyone(currentuser.user._id, currentuser.token, {
        id,
      });
      if (re?.message) {
        setUi((v) => !v);
        setExpandedUser(null);
        alert(re.message);
      } else if (re?.error) alert(re.error);
    } catch {
      alert("Error deleting user.");
    }
  };

  const toggleExpand = (id) => setExpandedUser(expandedUser === id ? null : id);

  const getRoleBadge = (role) => {
    const isAdmin = role === 0 || role === 2;
    return (
      <span
        style={{
          background: isAdmin ? "#fff3cd" : "#e8f5e9",
          color: isAdmin ? "#856404" : "#2e7d32",
          padding: "3px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {isAdmin ? "Admin" : "User"}
      </span>
    );
  };

  const PaginationBar = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      )
        pages.push(i);
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    const btnBase = {
      border: "1px solid #e0e0e0",
      borderRadius: "6px",
      padding: "6px 12px",
      fontSize: "13px",
      cursor: "pointer",
      fontWeight: 500,
      minWidth: 36,
    };
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 16,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 13, color: "#888" }}>
          Showing {(currentPage - 1) * USERS_PER_PAGE + 1}–
          {Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)} of{" "}
          {filteredUsers.length} users
        </span>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              ...btnBase,
              background: currentPage === 1 ? "#f5f5f5" : "#fff",
              color: currentPage === 1 ? "#ccc" : "#333",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
          >
            ‹ Prev
          </button>
          {pages.map((p, i) =>
            p === "..." ? (
              <span
                key={`e-${i}`}
                style={{ padding: "6px 4px", color: "#aaa", fontSize: 13 }}
              >
                …
              </span>
            ) : (
              <button
                key={p}
                onClick={() => goToPage(p)}
                style={{
                  ...btnBase,
                  background: currentPage === p ? "#E34607" : "#fff",
                  color: currentPage === p ? "#fff" : "#333",
                  border:
                    currentPage === p
                      ? "1px solid #E34607"
                      : "1px solid #e0e0e0",
                }}
              >
                {p}
              </button>
            ),
          )}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              ...btnBase,
              background: currentPage === totalPages ? "#f5f5f5" : "#fff",
              color: currentPage === totalPages ? "#ccc" : "#333",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next ›
          </button>
        </div>
      </div>
    );
  };

  const headerBg = isDeleteMode ? "#dc3545" : "#17a2b8";
  const headerLabel = isDeleteMode ? "Delete Users" : "Manage Users";
  const headerIcon = isDeleteMode ? "fa-user-minus" : "fa-users";

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        border: "1px solid #e0e0e0",
        marginBottom: "20px",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "#f8f9fa",
          color: "#2c3e50",
          borderBottom: "2px solid #e9ecef",
          padding: "16px 20px",
          borderRadius: "12px 12px 0 0",
          fontWeight: "600",
          fontSize: "18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <span>
          <i className={`fas ${headerIcon} me-2`}></i>
          {headerLabel}
          <span
            style={{
              marginLeft: 10,
              fontSize: 11,
              fontWeight: 500,
              background: isDeleteMode ? "#fde8ea" : "#e0f7fa",
              color: isDeleteMode ? "#dc3545" : "#17a2b8",
              padding: "3px 10px",
              borderRadius: 10,
            }}
          >
            {isDeleteMode ? "Delete Mode" : "View Mode"}
          </span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span
            style={{
              backgroundColor: headerBg,
              color: "#fff",
              padding: "5px 14px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {filteredUsers.length} / {alluser.length} users
          </span>
          <button
            onClick={onBack}
            style={{
              backgroundColor: "#6c757d",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "8px 16px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            <i className="fas fa-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>

      <div style={{ padding: "20px" }}>
        {/* Delete mode warning */}
        {isDeleteMode && (
          <div
            style={{
              background: "#fde8ea",
              border: "1px solid #f5c2c7",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 13,
              color: "#842029",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <i className="fas fa-exclamation-triangle"></i>
            You are in <strong>Delete Mode</strong> — deleted users cannot be
            recovered.
          </div>
        )}

        {/* Search */}
        <div style={{ position: "relative", marginBottom: "16px" }}>
          <i
            className="fas fa-search"
            style={{
              position: "absolute",
              left: 14,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#aaa",
              fontSize: 15,
            }}
          />
          <input
            type="text"
            placeholder="Search by name, mobile, email or address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "11px 14px 11px 40px",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              boxSizing: "border-box",
              background: "#fafbfc",
            }}
          />
          {searchQuery && (
            <i
              className="fas fa-times"
              onClick={() => setSearchQuery("")}
              style={{
                position: "absolute",
                right: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#aaa",
                cursor: "pointer",
                fontSize: 14,
              }}
            />
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>
            <i
              className="fas fa-spinner fa-spin fa-2x"
              style={{ color: "#E34607" }}
            ></i>
            <p style={{ marginTop: 12 }}>Loading users...</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="alert alert-danger" style={{ borderRadius: 8 }}>
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filteredUsers.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>
            <i
              className="fas fa-users fa-3x mb-3"
              style={{ color: "#ddd" }}
            ></i>
            <p style={{ marginTop: 12 }}>
              {searchQuery
                ? `No users found for "${searchQuery}"`
                : "No users found"}
            </p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && filteredUsers.length > 0 && (
          <>
            <div
              style={{
                borderRadius: "8px",
                border: "1px solid #e9ecef",
                overflow: "hidden",
              }}
            >
              {/* ── DELETE MODE header: Name | Email | Mobile | Role | Delete ── */}
              {isDeleteMode && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2.5fr 2fr 1.5fr 0.8fr 0.8fr",
                    background: "#dc3545",
                    color: "#fff",
                    padding: "12px 16px",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  <span>Name</span>
                  <span>Email</span>
                  <span>Mobile</span>
                  <span>Role</span>
                  <span style={{ textAlign: "center" }}>Delete</span>
                </div>
              )}

              {/* ── VIEW MODE header: Name | Email | Mobile | Role | View ── */}
              {/* FIXED: removed duplicate Mobile column and wrong Actions label */}
              {!isDeleteMode && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 2fr 1.5fr 0.8fr 0.6fr",
                    background: "#6c757d",
                    color: "#fff",
                    padding: "12px 16px",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  <span>Name</span>
                  <span>Email</span>
                  <span>Mobile</span>
                  <span>Role</span>
                  <span style={{ textAlign: "center" }}>View</span>
                </div>
              )}

              {/* Rows */}
              {paginatedUsers.map((item, index) => (
                <div key={item._id}>
                  {/* ── DELETE MODE ROW ── */}
                  {isDeleteMode && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2.5fr 2fr 1.5fr 0.8fr 0.8fr",
                        padding: "11px 16px",
                        borderBottom: "1px solid #f0f0f0",
                        backgroundColor:
                          index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: 14, fontWeight: 500 }}>
                        {item.Name}
                      </span>
                      <span style={{ fontSize: 13, color: "#666" }}>
                        {item.EmailId || (
                          <span style={{ color: "#ccc", fontStyle: "italic" }}>
                            —
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: 13, color: "#555" }}>
                        {item.MobileNo}
                      </span>
                      <span>{getRoleBadge(item.Role)}</span>
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <button
                          onClick={() => deleteoneuser(item._id, item.Name)}
                          title="Delete User"
                          style={{
                            background: "#dc3545",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            padding: "5px 12px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          <i className="fas fa-trash me-1"></i>Delete
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── VIEW MODE ROW — only eye button, NO delete button ── */}
                  {!isDeleteMode && (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2fr 2fr 1.5fr 0.8fr 0.6fr",
                          padding: "12px 16px",
                          borderBottom: "1px solid #f0f0f0",
                          backgroundColor:
                            expandedUser === item._id
                              ? "#fff5f2"
                              : index % 2 === 0
                                ? "#ffffff"
                                : "#f8f9fa",
                          alignItems: "center",
                          cursor: "pointer",
                          transition: "background 0.15s",
                        }}
                        onClick={() => toggleExpand(item._id)}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          <i
                            className={`fas fa-chevron-${expandedUser === item._id ? "down" : "right"} me-2`}
                            style={{ fontSize: 10, color: "#E34607" }}
                          />
                          {item.Name}
                        </span>
                        <span style={{ fontSize: 13, color: "#666" }}>
                          {item.EmailId || (
                            <span
                              style={{ color: "#ccc", fontStyle: "italic" }}
                            >
                              —
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: 13, color: "#555" }}>
                          {item.MobileNo}
                        </span>
                        <span>{getRoleBadge(item.Role)}</span>

                        {/* FIXED: only View (eye) button — no delete button in view mode */}
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => toggleExpand(item._id)}
                            title="View Details"
                            style={{
                              background: "#17a2b8",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "5px 12px",
                              cursor: "pointer",
                              fontSize: "12px",
                            }}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                        </div>
                      </div>

                      {/* Expanded detail panel — delete button kept here for quick access */}
                      {expandedUser === item._id && (
                        <div
                          style={{
                            background: "#fff9f7",
                            borderBottom: "2px solid #E34607",
                            padding: "16px 24px",
                            animation: "fadeIn 0.2s ease",
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(200px, 1fr))",
                              gap: "16px",
                            }}
                          >
                            {[
                              {
                                icon: "fa-user",
                                label: "Full Name",
                                value: item.Name,
                              },
                              {
                                icon: "fa-phone",
                                label: "Mobile",
                                value: item.MobileNo,
                              },
                              {
                                icon: "fa-envelope",
                                label: "Email",
                                value: item.EmailId || "Not provided",
                              },
                              {
                                icon: "fa-map-marker-alt",
                                label: "Address",
                                value: item.Address || "—",
                              },
                              {
                                icon: "fa-user-tag",
                                label: "Role",
                                value:
                                  item.Role === 0 || item.Role === 2
                                    ? "Admin"
                                    : "User",
                              },
                              {
                                icon: "fa-calendar",
                                label: "Joined",
                                value: item.createdAt
                                  ? new Date(item.createdAt).toLocaleDateString(
                                      "en-IN",
                                    )
                                  : "—",
                              },
                            ].map((field) => (
                              <div
                                key={field.label}
                                style={{
                                  background: "#fff",
                                  borderRadius: "8px",
                                  padding: "12px 14px",
                                  border: "1px solid #f0e0db",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "#E34607",
                                    fontWeight: 600,
                                    marginBottom: 4,
                                  }}
                                >
                                  <i className={`fas ${field.icon} me-1`}></i>
                                  {field.label}
                                </div>
                                <div
                                  style={{
                                    fontSize: 14,
                                    color: "#333",
                                    fontWeight: 500,
                                  }}
                                >
                                  {field.value}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div
                            style={{ marginTop: 14, display: "flex", gap: 8 }}
                          >
                            <button
                              onClick={() => setExpandedUser(null)}
                              style={{
                                background: "transparent",
                                color: "#888",
                                border: "1px solid #ddd",
                                borderRadius: "6px",
                                padding: "8px 16px",
                                cursor: "pointer",
                                fontSize: "13px",
                              }}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            <PaginationBar />
          </>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default DeleteUser;
